import { useState } from "react"
import { Box, Button, Card, CircularProgress, IconButton, Popover, TextField, Typography } from "@mui/material"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { Palette, ImageIcon, Upload } from "lucide-react"
import { type WebinarSection, headerImageOptions, toAbsoluteUrl, toPreviewImageUrl } from "./stock-images"

// The XOP brand mark shown in the subtitle bar — "the ball" (the navy + lime
// orbit mark, NO wordmark). We deliberately render this rather than the
// tenant's `logo_url`: the full XOP wordmark logo (which already bakes in the
// "XOP Networks" text) sitting next to the "XOP Networks" name label printed
// right beside it showed the company name TWICE — the duplication the user
// reported. The clean lockup is [ball] XOP Networks. Mirrors the olive
// newsletter builder, which was already corrected this way.
//
// It's a PNG (mail clients drop SVG) bundled in the support center's
// `/stock-images/`, so it resolves on the public host in the preview AND is
// CID-inlined into the sent email (`EmailImageInliner`, xop-brain) — the same
// path every other webinar image takes.
// Asset: xop-edge-customer/apps/portal-web/public/stock-images/xop-ball.png.
const XOP_BRAND_MARK_URL = "/stock-images/xop-ball.png"

// Editor UI for the email's header banner (subtitle, overlay text, banner image
// picker + custom upload). The actual email HTML is produced by `headerToHtml`
// below — the editor's preview imagery uses `?w=...` query params for thumbnails.
interface HeaderSectionProps {
  section: WebinarSection
  bgColor: string
  customHeaderImages: Array<{ id: number; url: string; name: string }>
  isUploadingHeaderImage: boolean
  headerImageInputRef: React.RefObject<HTMLInputElement | null>
  onUpdate: (sectionId: string, partial: any) => void
  onChangeBgColor: (sectionId: string, color: string) => void
  onHeaderImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export function HeaderSection({
  section,
  bgColor,
  customHeaderImages,
  isUploadingHeaderImage,
  headerImageInputRef,
  onUpdate,
  onChangeBgColor,
  onHeaderImageUpload,
}: HeaderSectionProps) {
  const [colorAnchor, setColorAnchor] = useState<null | HTMLElement>(null)
  return (
    <Card key={section.id} sx={{ borderRadius: 0 }} style={{ backgroundColor: bgColor, marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
            <ImageIcon size={20} />
            Header Banner
          </Typography>
          <IconButton size="small" onClick={(e) => setColorAnchor(e.currentTarget)}>
            <Palette size={16} />
          </IconButton>
          <Popover
            open={Boolean(colorAnchor)}
            anchorEl={colorAnchor}
            onClose={() => setColorAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { width: 256, p: 2 } } }}
          >
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Background Color</Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              {['#ffffff', '#f8fafc', '#eff6ff', '#f0f9ff', '#ecfdf5'].map(color => (
                <Box
                  component="button"
                  key={color}
                  sx={{ width: 32, height: 32, borderRadius: 1, border: "2px solid", cursor: "pointer" }}
                  style={{ backgroundColor: color, borderColor: bgColor === color ? '#2563eb' : '#e5e7eb' }}
                  onClick={() => onChangeBgColor(section.id, color)}
                />
              ))}
            </Box>
          </Popover>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`header-subtitle-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Subtitle</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="webinar banner subtitle - create a compelling subtitle that encourages attendance"
                  placeholder="e.g., join our exclusive online event"
                  currentValue={section.content.subtitle}
                  onGenerate={(generated) => onUpdate(section.id, { subtitle: generated })}
                />
                <AiTextImprover
                  fieldType="short"
                  context="webinar banner subtitle"
                  currentText={section.content.subtitle}
                  onImprove={(improved) => onUpdate(section.id, { subtitle: improved })}
                />
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              id={`header-subtitle-${section.id}`}
              value={section.content.subtitle}
              onChange={(e) => onUpdate(section.id, { subtitle: e.target.value })}
              placeholder="Subtitle"
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor={`overlay-text-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Image Overlay Text</Typography>
            <TextField
              fullWidth
              size="small"
              id={`overlay-text-${section.id}`}
              value={section.content.overlayText || ''}
              onChange={(e) => onUpdate(section.id, { overlayText: e.target.value })}
              placeholder="e.g., Reminder: Webinar starts tomorrow!"
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>Text displayed as a banner on top of the header image (leave empty to hide)</Typography>
          </Box>

          <Box>
            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500 }}>Banner Image</Typography>
            <Box sx={{ mt: 1 }}>
              <Box
                component="img"
                // Anchor the chosen banner (a `/stock-images/*` stock photo or a
                // `/api/v1/image/*` upload) to a reachable origin — a raw relative
                // src 404s to a broken placeholder on the SPA origin / desktop
                // `tauri://localhost`. See `toPreviewImageUrl` in ./stock-images.
                src={toPreviewImageUrl(section.content.imageUrl) + '?w=600&h=200&fit=crop'}
                alt="Header"
                sx={{ width: "100%", height: 128, objectFit: "cover", borderRadius: 1, mb: 1.5, display: "block" }}
              />

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, mb: 1.5 }}>
                {[...headerImageOptions, ...customHeaderImages].map(img => (
                  <Box
                    component="img"
                    key={img.id}
                    src={toPreviewImageUrl(img.url) + '?w=150&h=100&fit=crop'}
                    alt={img.name}
                    onClick={() => onUpdate(section.id, { imageUrl: img.url })}
                    sx={{
                      width: "100%",
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 1,
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor: section.content.imageUrl === img.url ? "#3b82f6" : "divider",
                    }}
                  />
                ))}
              </Box>

              <input
                ref={headerImageInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onHeaderImageUpload}
              />
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                fullWidth
                onClick={() => headerImageInputRef.current?.click()}
                disabled={isUploadingHeaderImage}
                startIcon={isUploadingHeaderImage ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
              >
                {isUploadingHeaderImage ? "Uploading..." : "Upload Custom Image"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

// Build the header rows of the rendered email HTML. Includes:
//   * optional red overlay banner above the hero
//   * hero image locked to a 650×325 (2:1) banner via width/height ATTRIBUTES
//     (Outlook) + max-height + object-fit:cover (modern clients), sized directly
//     on the <img> — no `max-height;overflow:hidden` wrapper <div> (Gmail/Outlook
//     strip those off <div>s, which is what made a square upload render
//     full-height AND leave a right-edge width gap). `width:100%` keeps it
//     edge-to-edge in every client.
//   * subtitle bar with tenant logo on the left and section subtitle on the right
//
// The td/img around the hero use `font-size:0; line-height:0;` plus
// `vertical-align:bottom; margin:0; padding:0;` on the <img> to eliminate the
// inline-image whitespace gap that otherwise appears between hero and subtitle
// bar in some clients (upstream fix: 5cedbd0).
export function headerToHtml(
  section: WebinarSection,
  tenantName: string,
  // `_tenantLogo` is kept in the signature for caller compatibility but no
  // longer drives the header mark — we always render the XOP ball (not the
  // squeezed full wordmark, which duplicated the "XOP Networks" name label).
  // See XOP_BRAND_MARK_URL above.
  _tenantLogo?: string | null | undefined
): string {
  const headerImageUrl = toAbsoluteUrl(section.content.imageUrl)
  const overlayText = section.content.overlayText || ''
  // The brand mark ("the ball"), anchored absolute to the public host so the
  // mail client (and EmailImageInliner) can fetch + CID-inline it.
  const brandMarkUrl = toAbsoluteUrl(XOP_BRAND_MARK_URL)

  return `
          <!-- Header -->
          <tr>
            <td style="padding: 0; font-size: 0; line-height: 0; mso-line-height-rule: exactly;">
              ${overlayText ? `
              <!-- Overlay Text Banner -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#e53e3e" style="background-color: #e53e3e;">
                <tr>
                  <td style="padding: 12px 40px; text-align: center;" align="center">
                    <p style="color: #ffffff; font-size: 16px; font-weight: bold; font-family: Arial, Helvetica, sans-serif; margin: 0; line-height: 22px;">${overlayText}</p>
                  </td>
                </tr>
              </table>
              ` : ''}
              <!-- Hero Image -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; width: 100%;">
                <tr>
                  <td style="padding: 0; font-size: 0; line-height: 0; width: 100%;" width="100%">
                    <!--
                      Fixed-height banner. The height cap lives ON THE img itself
                      (width/height ATTRIBUTES for Outlook's Word engine + max-height
                      for modern clients; object-fit:cover crops rather than squishes
                      wherever it's honored). It deliberately does NOT wrap the image in
                      a div with max-height + overflow:hidden: Gmail (web) and Outlook
                      strip max-height/overflow off divs, which left a full-height
                      SQUARE image AND a right-edge white gap (the clipped div stopped
                      tracking the container width). width:100% makes the image fill
                      the container edge-to-edge in every client. 650x325 = a clean 2:1
                      banner (matches the bundled 800x400 stock photos exactly). Mirrors
                      the olive-newsletter hero. See docs/marketing.md.
                    -->
                    <img src="${headerImageUrl}"
                         alt="Webinar Banner"
                         width="650" height="325"
                         style="width: 100%; height: auto; max-height: 325px; object-fit: cover; display: block; border: 0; outline: none; text-decoration: none; vertical-align: bottom; margin: 0; padding: 0;" />
                  </td>
                </tr>
              </table>
              <!-- Subtitle Bar with Logo -->
              <table width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#8a95a5" style="background-color: #8a95a5;">
                <tr>
                  <td class="mobile-pad" style="padding: 20px 40px;">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td class="mobile-stack mobile-center" width="50%" style="width: 50%; vertical-align: middle; text-align: left;" valign="middle" align="left">
                          <table cellpadding="0" cellspacing="0" border="0" style="display: inline-block;">
                            <tr>
                              <td style="vertical-align: middle; padding-right: 15px;" valign="middle">
                                <img src="${brandMarkUrl}" alt="${tenantName}" height="45" style="height: 45px; width: auto; display: block; border: 0;" />
                              </td>
                              <td style="vertical-align: middle;" valign="middle">
                                <p style="color: #ffffff; font-size: 20px; font-weight: bold; font-family: Arial, Helvetica, sans-serif; margin: 0; line-height: 26px;">${tenantName}</p>
                              </td>
                            </tr>
                          </table>
                        </td>
                        <td class="mobile-stack mobile-center" width="50%" style="width: 50%; vertical-align: middle; text-align: right;" valign="middle" align="right">
                          <p style="color: #ffffff; font-size: 18px; font-weight: bold; font-family: Arial, Helvetica, sans-serif; margin: 0; line-height: 24px;">${section.content.subtitle}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          `
}
